import Head from 'next/head';
import { useRouter } from 'next/router';
import LD, {
  BlogPostMeta,
  FAQPageMeta,
  PodcastEpisodeMeta,
  ServiceMeta,
} from '../../../components/layout/Head/returnLd';
import { useMemo } from 'react';
import { SITE_URL } from '../../../utils/constants';

// Palabras clave predeterminadas para SEO
const es_default_keywords =
  'diseño, estudio, studio, acueducto, cdmx, innovación, diseño estratégico, diseño de experiencia, diseño de producto, diseño de servicio, diseño de estrategia, tecnología';
const en_default_keywords =
  'design, studio, acueducto, cdmx, innovation, strategic design, experience design, product design, brand design, design strategy, technology';

// Definición de tipos para las props del componente
export type HeadProps = {
  title: string | undefined;
  description: string | undefined;
  headerTitle: string;
  keywords?: string;
  en_canonical?: string;
  es_canonical?: string;
  image?: { fileName: string; alt: string | undefined };
  noIndex?: boolean;
  blogPost?: BlogPostMeta;
  podcastEpisode?: PodcastEpisodeMeta;
  service?: ServiceMeta;
  faqPage?: FAQPageMeta;
  /**
   * Override the canonical site root used in JSON-LD page URLs. Pass PODCAST_URL from the
   * podcast app so its WebPage / BreadcrumbList / PodcastEpisode entries point to
   * cuandoelriosuena.com instead of acueducto.com. Defaults to the studio's SITE_URL/locale.
   */
  siteRoot?: string;
};

const NewHead = ({
  title,
  description,
  headerTitle,
  keywords,
  en_canonical,
  es_canonical,
  image,
  noIndex,
  blogPost,
  podcastEpisode,
  service,
  faqPage,
  siteRoot,
}: HeadProps) => {
  const router = useRouter();
  const { locale } = router;

  // Determina si el idioma actual es español
  const isSpanish = locale === 'es';

  // URL canonical de la página actual según locale. Se pasa a returnLd para que las URLs
  // dentro del JSON-LD (WebPage, BreadcrumbList, BlogPosting, PodcastEpisode) reflejen la
  // canonical real del usuario y no el path post-rewrite del middleware (ej. `/es/work/<x>`
  // en lugar de `/es/portafolio/<x>`). Si falta el canonical para el locale actual, returnLd
  // cae al construct heredado `${siteRoot}${router.asPath}`.
  const canonicalUrl = isSpanish ? es_canonical : en_canonical;

  // Memoriza la imagen OG para evitar recálculos innecesarios
  const ogImage = useMemo(() => {
    if (!image)
      return {
        url: `${SITE_URL}/assets/img/og/og_main.gif`,
        alt: 'Acueducto Studio',
      };

    // Si fileName ya es una URL absoluta (Prismic, o un app fuera de acueducto.com
    // como el calculator), úsala tal cual. Si no, ensambla con la estructura legacy
    // de studio (`acueducto.com/assets/img/og/...`).
    return {
      url:
        image.fileName.startsWith('http') ||
        image.fileName.includes('images.prismic.io')
          ? image.fileName
          : `${SITE_URL}/assets/img/og/${image.fileName}`,
      alt: image.alt,
    };
  }, [image]);

  return (
    <Head>
      {/* Metadatos básicos */}
      <meta charSet="utf-8" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no,viewport-fit=cover, user-scalable=no"
      />
      <title>{title}</title>

      {/* Metadatos para Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content="@acueductostudio" />
      <meta name="twitter:site" content="@acueductostudio" />

      {/* Metadatos para SEO y Open Graph */}
      <meta name="description" content={description} />
      <meta
        name="og:description"
        property="og:description"
        content={description}
      />
      <meta name="og:title" property="og:title" content={title} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Acueducto" />

      {/* Metadato para no indexar si es necesario */}
      {noIndex && <meta name="robots" content="noindex, follow" />}

      {/* Metadatos específicos del idioma */}
      {isSpanish ? (
        <>
          <meta property="og:locale" content="es_MX" />
          {en_canonical && (
            <meta property="og:locale:alternate" content="en_US" />
          )}
          <meta property="og:url" content={es_canonical} />
          <meta name="keywords" content={keywords || es_default_keywords} />
        </>
      ) : (
        <>
          <meta property="og:locale" content="en_US" />
          {es_canonical && (
            <meta property="og:locale:alternate" content="es_MX" />
          )}
          <meta property="og:url" content={en_canonical} />
          <meta name="keywords" content={keywords || en_default_keywords} />
        </>
      )}

      {/* Metadatos para la imagen OG */}
      <meta property="og:image" content={ogImage.url} />
      <meta property="og:image:alt" content={ogImage.alt} />

      {/* Enlaces canónicos y alternativos */}
      {isSpanish ? (
        <>
          <link rel="canonical" href={es_canonical} />
          {en_canonical && (
            <>
              <link rel="alternate" hrefLang="en" href={en_canonical} />
              <link rel="alternate" hrefLang="es" href={es_canonical} />
              <link rel="alternate" href={en_canonical} hrefLang="x-default" />
            </>
          )}
        </>
      ) : (
        <>
          <link rel="canonical" href={en_canonical} />
          {es_canonical && (
            <>
              <link rel="alternate" hrefLang="es" href={es_canonical} />
              <link rel="alternate" hrefLang="en" href={en_canonical} />
              <link rel="alternate" href={en_canonical} hrefLang="x-default" />
            </>
          )}
        </>
      )}

      {/* Script para datos estructurados */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: LD(
            locale,
            router.asPath,
            description,
            title,
            headerTitle,
            image,
            blogPost,
            podcastEpisode,
            { siteRoot, canonicalUrl, service, faqPage },
          ),
        }}
      />

      {/* Preconexiones para mejorar el rendimiento */}
      <link rel="preconnect" href="https://connect.facebook.net" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
    </Head>
  );
};

export default NewHead;
